import * as React from 'react';

import {
	Combobox,
	ComboboxItem,
	type ComboboxItemProps,
	ComboboxPopover,
	ComboboxProvider,
	Portal,
	useComboboxContext,
	useComboboxStore,
} from '@ariakit/react';
import { filterWords } from '@platejs/combobox';
import { useComboboxInput, type UseComboboxInputResult, useHTMLInputCursorState } from '@platejs/combobox/react';

import { theme } from 'antd';
import cn from 'classnames';
import type { Point, TElement } from 'platejs';
import { useComposedRef, useEditorRef } from 'platejs/react';

import styles from './InlineCombobox.module.scss';

type FilterFn = (
	item: { value: string; group?: string; keywords?: string[]; label?: string },
	search: string,
) => boolean;

type InlineComboboxContextValue = {
	filter: FilterFn | false;
	inputProps: UseComboboxInputResult['props'];
	inputRef: React.RefObject<HTMLInputElement | null>;
	removeInput: UseComboboxInputResult['removeInput'];
	showTrigger: boolean;
	trigger: string;
	setHasEmpty: (hasEmpty: boolean) => void;
};

const InlineComboboxContext = React.createContext<InlineComboboxContextValue>(
	null as unknown as InlineComboboxContextValue,
);

const defaultFilter: FilterFn = ({ group, keywords = [], label, value }, search) => {
	const uniqueTerms = new Set([value, ...keywords, group, label].filter(Boolean));

	return Array.from(uniqueTerms).some((keyword) => filterWords(keyword!, search));
};

type InlineComboboxProps = {
	children: React.ReactNode;
	element: TElement;
	trigger: string;
	filter?: FilterFn | false;
	hideWhenNoValue?: boolean;
	showTrigger?: boolean;
	value?: string;
	setValue?: (value: string) => void;
};

const InlineCombobox = ({
	children,
	element,
	filter = defaultFilter,
	hideWhenNoValue = false,
	setValue: setValueProp,
	showTrigger = true,
	trigger,
	value: valueProp,
}: InlineComboboxProps) => {
	const editor = useEditorRef();
	const inputRef = React.useRef<HTMLInputElement>(null);
	const cursorState = useHTMLInputCursorState(inputRef);

	const [valueState, setValueState] = React.useState('');
	const hasValueProp = valueProp !== undefined;
	const value = hasValueProp ? valueProp : valueState;

	// Check if current user is the creator of this element (for Yjs collaboration)
	const isCreator = React.useMemo(() => {
		const elementUserId = (element as unknown as { userId: string }).userId;
		const currentUserId = editor.meta.userId;

		// If no userId (backwards compatibility or non-Yjs), allow
		if (!elementUserId) return true;

		return elementUserId === currentUserId;
	}, [editor.meta.userId, element]);

	const setValue = React.useCallback(
		(newValue: string) => {
			setValueProp?.(newValue);

			if (!hasValueProp) {
				setValueState(newValue);
			}
		},
		[setValueProp, hasValueProp],
	);

	/**
	 * Track the point just before the input element so we know where to
	 * insertText if the combobox closes due to a selection change.
	 */
	const insertPoint = React.useRef<Point | null>(null);

	React.useEffect(() => {
		const path = editor.api.findPath(element);

		if (!path) return;

		const point = editor.api.before(path);

		if (!point) return;

		const pointRef = editor.api.pointRef(point);
		insertPoint.current = pointRef.current;

		return () => {
			pointRef.unref();
		};
	}, [editor, element]);

	const { props: inputProps, removeInput } = useComboboxInput({
		cancelInputOnBlur: true,
		cursorState,
		autoFocus: isCreator,
		ref: inputRef,
		onCancelInput: (cause) => {
			if (cause !== 'backspace') {
				editor.tf.insertText(trigger + value, {
					at: insertPoint?.current ?? undefined,
				});
			}
			if (cause === 'arrowLeft' || cause === 'arrowRight') {
				editor.tf.move({
					distance: 1,
					reverse: cause === 'arrowLeft',
				});
			}
		},
	});

	const [hasEmpty, setHasEmpty] = React.useState(false);

	const contextValue: InlineComboboxContextValue = React.useMemo(
		() => ({
			filter,
			inputProps,
			inputRef,
			removeInput,
			setHasEmpty,
			showTrigger,
			trigger,
		}),
		[trigger, showTrigger, filter, inputRef, inputProps, removeInput, setHasEmpty],
	);

	const store = useComboboxStore({
		// open: ,
		setValue: (newValue) => React.startTransition(() => setValue(newValue)),
	});

	const items = store.useState('items');

	/**
	 * If there is no active ID and the list of items changes, select the first
	 * item.
	 */
	React.useEffect(() => {
		if (!store.getState().activeId) {
			store.setActiveId(store.first());
		}
	}, [items, store]);

	return (
		<span contentEditable={false}>
			<ComboboxProvider open={(items.length > 0 || hasEmpty) && (!hideWhenNoValue || value.length > 0)} store={store}>
				<InlineComboboxContext.Provider value={contextValue}>{children}</InlineComboboxContext.Provider>
			</ComboboxProvider>
		</span>
	);
};

const InlineComboboxInput = ({
	className,
	ref: propRef,
	...props
}: React.HTMLAttributes<HTMLInputElement> & {
	ref?: React.RefObject<HTMLInputElement | null>;
}) => {
	const { inputProps, inputRef: contextRef, showTrigger, trigger } = React.useContext(InlineComboboxContext);

	const store = useComboboxContext()!;
	const value = store.useState('value');

	const ref = useComposedRef(propRef, contextRef);

	/**
	 * To create an auto-resizing input, we render a visually hidden span
	 * containing the input value and position the input element on top of it.
	 * This works well for all cases except when input exceeds the width of the
	 * container.
	 */

	return (
		<>
			{showTrigger && trigger}

			<span className={styles.inputContainer}>
				<span className={styles.inputValue} aria-hidden="true">
					{value || '\u200B'}
				</span>

				<Combobox
					ref={ref}
					className={cn(styles.input, className)}
					value={value}
					autoSelect
					{...inputProps}
					{...props}
				/>
			</span>
		</>
	);
};

const InlineComboboxContent: typeof ComboboxPopover = ({ className, ...props }) => {
	const {
		token: { colorBgLayout, borderRadius },
	} = theme.useToken();

	return (
		<Portal>
			<ComboboxPopover
				className={cn(styles.popover, className)}
				style={{ borderRadius, backgroundColor: colorBgLayout }}
				{...props}
			/>
		</Portal>
	);
};

const InlineComboboxItem = ({
	className,
	focusEditor = true,
	group,
	keywords,
	label,
	onClick,
	...props
}: {
	focusEditor?: boolean;
	group?: string;
	keywords?: string[];
	label?: string;
} & ComboboxItemProps &
	Required<Pick<ComboboxItemProps, 'value'>>) => {
	const { value } = props;

	const { filter, removeInput } = React.useContext(InlineComboboxContext);

	const store = useComboboxContext()!;

	// Optimization: Do not subscribe to value if filter is false
	const search = filter && store.useState('value');

	const visible = React.useMemo(
		() => !filter || filter({ group, keywords, label, value }, search as string),
		[filter, group, keywords, label, value, search],
	);

	const {
		token: { colorTextBase },
	} = theme.useToken();

	if (!visible) return null;

	return (
		<ComboboxItem
			className={cn(styles.item, className)}
			style={{ color: colorTextBase }}
			onClick={(event) => {
				removeInput(focusEditor);
				onClick?.(event);
			}}
			{...props}
		/>
	);
};

const InlineComboboxEmpty = ({ children, className }: React.HTMLAttributes<HTMLDivElement>) => {
	const { setHasEmpty } = React.useContext(InlineComboboxContext);
	const store = useComboboxContext()!;
	const items = store.useState('items');

	React.useEffect(() => {
		setHasEmpty(true);

		return () => {
			setHasEmpty(false);
		};
	}, [setHasEmpty]);

	const {
		token: { colorTextBase },
	} = theme.useToken();

	if (items.length > 0) return null;

	return (
		<div className={cn(styles.item, styles.empty, className)} style={{ color: colorTextBase }}>
			{children}
		</div>
	);
};

export { InlineCombobox, InlineComboboxContent, InlineComboboxEmpty, InlineComboboxInput, InlineComboboxItem };
